import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import Avatar from '@shared/ui/Avatar';
import { IgLink } from '@shared/ui/IgLink';
import CreatorCardModal from './CreatorCardModal';

interface Fmt { type: string; n: number; avg_eng: number | null; avg_views: number | null }
interface Growth { series: unknown[]; formats: Fmt[] }
interface PeerRow { provider_id: string; handle: string; avatar: string | null; followers: number | null; er: number | null; rank: number; is_me: boolean }
interface Bench { me: { provider_id: string; handle: string; avatar: string | null; followers: number | null; er: number | null; rank: number; out_of: number } | null; top: PeerRow[]; scope: 'niche' | 'size' }

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);

// Growth detail: which formats perform + the peer leaderboard. Embedded at the
// bottom of the creator Home (the follower trend lives in Home's hero), so this
// is no longer a standalone tab. Each section renders once its data arrives.
export default function CreatorGrow() {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string) => t(k, { defaultValue: d });

  const q = useSupabaseQuery<Growth | null>(['creator-growth'], async () => supabase.rpc('creator_growth_series', {}));
  const g = q.data;
  const pq = useSupabaseQuery<Bench | null>(['creator-peers'], async () => supabase.rpc('creator_peer_benchmark', { p_limit: 5 }));
  const bench = pq.data;
  const [card, setCard] = useState<{ providerId: string; handle: string } | null>(null);

  const reel = g?.formats.find((f) => f.type === 'reel');
  const photo = g?.formats.find((f) => f.type === 'photo');
  const maxEng = Math.max(reel?.avg_eng ?? 0, photo?.avg_eng ?? 0, 1);

  const bar = (label: string, v: number | null, n: number, color: string) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
        <span>{label} <span style={{ color: 'var(--dim)' }}>· {n}</span></span><span>{fmtK(v)}</span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 6 }}>
        <div style={{ width: `${Math.round(((v ?? 0) / maxEng) * 100)}%`, height: '100%', background: color, borderRadius: 6 }} />
      </div>
    </div>
  );

  const peerRow = (r: { providerId: string; handle: string; avatar: string | null; followers: number | null; er: number | null; rank: number; isMe: boolean; outOf?: number }) => (
    <div key={`${r.rank}-${r.handle}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.05)', background: r.isMe ? 'rgba(74,158,255,0.12)' : 'transparent' }}>
      <span style={{ width: 18, color: 'var(--dim)', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{r.rank}</span>
      <div onClick={() => setCard({ providerId: r.providerId, handle: r.handle })} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <Avatar src={r.avatar} name={r.handle} size={30} />
        <span style={{ minWidth: 0, fontSize: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ color: r.isMe ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: r.isMe ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{r.handle}</span>
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}><IgLink handle={r.handle} size={14} /></span>
          {(r.isMe || r.outOf) && <span style={{ color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>{r.isMe ? ` · ${tr('creator.grow.you', 'вы')}` : ''}{r.outOf ? ` · ${t('creator.grow.ofN', { defaultValue: 'из {{n}}', n: r.outOf })}` : ''}</span>}
        </span>
      </div>
      <span style={{ color: 'var(--muted)', fontSize: 12, width: 44, textAlign: 'right', flexShrink: 0 }}>{fmtK(r.followers)}</span>
      <span style={{ color: '#8bd450', fontSize: 13, fontWeight: 600, width: 44, textAlign: 'right', flexShrink: 0 }}>{r.er != null ? `${Number(r.er).toFixed(1)}%` : '—'}</span>
    </div>
  );

  return (
    <div>
      {(reel || photo) && (
        <>
          <h3 style={{ color: 'var(--text)', fontSize: 15, margin: '20px 0 4px' }}>{tr('creator.grow.formats', 'Что заходит лучше')}</h3>
          <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 12px' }}>{tr('creator.grow.formatsSub', 'Средняя вовлечённость (лайки + комменты) по форматам')}</p>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14 }}>
            {reel && bar(tr('creator.menu.reel', 'Рилс'), reel.avg_eng, reel.n, '#4a9eff')}
            {photo && bar(tr('creator.grow.photo', 'Фото / карусель'), photo.avg_eng, photo.n, '#8a5cff')}
          </div>
        </>
      )}

      {bench && bench.top.length > 0 && (
        <div id="creator-peers">
          <h3 style={{ color: 'var(--text)', fontSize: 15, margin: '20px 0 4px' }}>{tr('creator.grow.peersTitle', 'Среди похожих')}</h3>
          <p style={{ color: 'var(--dim)', fontSize: 12.5, margin: '0 0 12px' }}>{bench.scope === 'niche' ? tr('creator.grow.peersSubNiche', 'По вовлечённости в вашей нише') : tr('creator.grow.peersSub', 'По вовлечённости среди блогеров вашего размера')}</p>
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
            {bench.top.map((r) => peerRow({ providerId: r.provider_id, handle: r.handle, avatar: r.avatar, followers: r.followers, er: r.er, rank: r.rank, isMe: r.is_me }))}
            {bench.me && !bench.top.some((r) => r.is_me) && (
              <>
                <div style={{ textAlign: 'center', color: 'var(--dim)', fontSize: 14, letterSpacing: 2, padding: '2px 0' }}>···</div>
                {peerRow({ providerId: bench.me.provider_id, handle: bench.me.handle, avatar: bench.me.avatar, followers: bench.me.followers, er: bench.me.er, rank: bench.me.rank, isMe: true, outOf: bench.me.out_of })}
              </>
            )}
          </div>
          <p style={{ color: 'var(--dim)', fontSize: 11, margin: '6px 2px 0' }}>{bench.scope === 'niche'
            ? tr('creator.grow.nicheNote', '«Похожие» = блогеры вашей ниши с близким числом подписчиков (½–2×).')
            : tr('creator.grow.sizeNote', '«Ваш размер» = блогеры примерно с вашим числом подписчиков (½–2×).')}</p>
        </div>
      )}

      {card && <CreatorCardModal providerId={card.providerId} handle={card.handle} onClose={() => setCard(null)} />}
    </div>
  );
}
